<template>
  <div class="revenda-clientes-page">
    <section class="reseller-summary">
      <div>
        <span class="summary-kicker">Revenda</span>
        <h2>{{ revenda.nome || "Revenda não informada" }}</h2>
        <div class="summary-details">
          <span>Endereço: {{ enderecoRevenda }}</span>
          <span>Telefone: {{ revenda.fone || "Não informado" }}</span>
        </div>
      </div>
      <router-link
        class="btn btn-clean font-weight-bold"
        :to="{ name: $route.params.rota }"
      >
        Voltar
      </router-link>
    </section>

    <section class="metric-grid">
      <div class="metric-card">
        <span>Total de clientes</span>
        <strong>{{ metricas.total }}</strong>
      </div>
      <div class="metric-card">
        <span>Ativos</span>
        <strong>{{ metricas.ativos }}</strong>
      </div>
      <div class="metric-card">
        <span>Bloqueados</span>
        <strong>{{ metricas.bloqueados }}</strong>
      </div>
      <div class="metric-card">
        <span>Com apps</span>
        <strong>{{ metricas.comApps }}</strong>
      </div>
    </section>

    <section class="list-controls">
      <b-form-input
        v-model="busca"
        class="search-input"
        placeholder="Buscar cliente, CNPJ, cidade ou plugin..."
      />
      <div class="filter-chips">
        <button
          v-for="filtro in filtrosRapidos"
          :key="filtro.value"
          type="button"
          class="filter-chip"
          :class="{ ativo: filtroRapido === filtro.value }"
          @click="filtroRapido = filtro.value"
        >
          {{ filtro.text }}
        </button>
      </div>
    </section>

    <section class="table-shell">
      <div v-if="!clientes.length" class="empty-state">
        Nenhum cliente vinculado a esta revenda.
      </div>
      <div v-else-if="!clientesFiltrados.length" class="empty-state">
        Nenhum cliente encontrado para sua busca.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover reseller-client-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Documento</th>
              <th>Contato</th>
              <th>Plano / Validade</th>
              <th>Apps</th>
              <th>Licença</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cliente in clientesFiltrados" :key="clienteKey(cliente)">
              <td>
                <div class="client-name">{{ cliente.cliente || "Sem nome" }}</div>
                <div class="table-muted">
                  {{ cidadeUfCliente(cliente) }}
                </div>
                <div class="table-muted">
                  Data criação:
                  {{ formatarDataLista(cliente.data_criacao || cliente.data_cadastro) }}
                </div>
              </td>
              <td>
                <strong>{{ documentoCliente(cliente) }}</strong>
              </td>
              <td>
                <div>{{ cliente.fone || "Não informado" }}</div>
                <div class="table-muted" v-if="cliente.email">{{ cliente.email }}</div>
              </td>
              <td>
                <div class="plan-name">{{ nomePlanoLicenca(cliente) }}</div>
                <div class="table-muted">
                  Vence: {{ formatarDataLista(dataValidadeLicenca(cliente)) }}
                </div>
                <div class="table-muted">
                  Pagamento: {{ formatarDataLista(cliente.data_pagamento) }}
                </div>
              </td>
              <td>
                <div v-if="totalAppsAtivosLicenca(cliente) > 0">
                  <div class="apps-count">
                    {{ totalAppsAtivosLicenca(cliente) }} apps ativos
                  </div>
                  <div class="apps-names">
                    {{ nomesAppsResumo(cliente) }}
                  </div>
                  <button
                    v-if="appsAtivosLicenca(cliente).length"
                    type="button"
                    class="btn btn-xs btn-light-primary mt-2"
                    @click="abrirModalApps(cliente)"
                  >
                    Ver apps
                  </button>
                </div>
                <span v-else class="table-muted font-weight-bold">
                  Sem apps ativos
                </span>
              </td>
              <td>
                <div class="license-line">
                  <span>Anual</span>
                  <strong>
                    {{ formatarMoedaLicenca(totalAnualLicenca(cliente)) }}
                  </strong>
                </div>
                <div class="license-line">
                  <span>Mensal</span>
                  <strong>
                    {{ formatarMoedaLicenca(totalMensalRecorrenteLicenca(cliente)) }}/mês
                  </strong>
                </div>
              </td>
              <td>
                <div class="status-stack">
                  <span
                    class="label label-inline"
                    :class="
                      clienteAtivo(cliente)
                        ? 'label-light-success'
                        : 'label-light-danger'
                    "
                  >
                    {{ clienteAtivo(cliente) ? "Ativo" : "Inativo" }}
                  </span>
                  <span
                    class="label label-inline"
                    :class="
                      clienteBloqueado(cliente)
                        ? 'label-light-danger'
                        : 'label-light-success'
                    "
                  >
                    {{ clienteBloqueado(cliente) ? "Bloqueado" : "Não bloqueado" }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <b-modal
      id="modal-apps-cliente-revenda"
      :title="
        clienteAppsSelecionado
          ? 'Apps do cliente - ' + clienteAppsSelecionado.cliente
          : 'Apps do cliente'
      "
      hide-footer
      centered
      size="lg"
    >
      <div v-if="!appsClienteSelecionado.length" class="empty-state compact">
        Nenhum app ativo para este cliente.
      </div>
      <div v-else class="apps-modal-list">
        <div
          v-for="(app, index) in appsClienteSelecionado"
          :key="'app_' + index"
          class="app-detail-item"
        >
          <div>
            <strong>{{ nomeAppLicenca(app) }}</strong>
            <span class="app-type">{{ tipoAppLicenca(app) }}</span>
            <div class="table-muted">Origem: {{ origemAppLicenca(app) }}</div>
          </div>
          <div class="app-price">
            {{ textoValorAppLicenca(app) }}
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modal-gestao-apps-cliente-revenda"
      :title="
        clienteAppsGestao
          ? 'Apps do cliente - ' + (clienteAppsGestao.cliente || clienteAppsGestao.nome)
          : 'Apps do cliente'
      "
      hide-footer
      centered
      size="xl"
    >
      <div class="apps-gestao-modal">
        <div class="apps-gestao-header">
          <div>
            <span class="table-muted d-block">Saldo disponível da revenda</span>
            <strong class="apps-gestao-saldo">{{ formatarMoedaLicenca(saldoAppsRevenda.disponivel) }}</strong>
            <span class="table-muted d-block mt-1">
              Licenças disponíveis: {{ licencasMultiempresaDisponiveis }}
            </span>
          </div>
          <div>
            <span class="table-muted d-block">Cliente selecionado</span>
            <strong>{{ clienteAppsGestao ? clienteAppsGestao.cliente || clienteAppsGestao.nome : "Não informado" }}</strong>
          </div>
        </div>

        <div v-if="loadingAppsClienteGestao" class="empty-state compact">
          Carregando apps disponíveis...
        </div>
        <div v-else-if="!featuresGestaoCliente.length" class="empty-state compact">
          Nenhum módulo/plugin disponível para este cliente.
        </div>
        <div v-else class="apps-gestao-list">
          <div
            class="apps-gestao-item"
            v-for="feature in featuresGestaoCliente"
            :key="feature.code"
          >
            <div class="apps-gestao-main">
              <b-form-checkbox v-model="feature.active" switch class="mr-3 mt-1" />
              <div>
                <strong>{{ feature.name }}</strong>
                <span class="app-type ml-2">
                  {{ feature.type === "plugin" ? "Plugin" : "Módulo" }}
                </span>
                <div class="table-muted">{{ formatarMoedaLicenca(feature.monthly_price) }}/mês</div>
              </div>
            </div>

            <div class="apps-gestao-controls">
              <div>
                <label class="apps-gestao-label">Período (meses)</label>
                <b-form-select
                  v-model.number="feature.months"
                  :disabled="!feature.active"
                  :options="opcoesPeriodoMeses"
                />
              </div>

              <div v-if="feature.billing_mode === 'per_quantity'">
                <label class="apps-gestao-label">Quantidade</label>
                <b-form-input
                  v-model.number="feature.quantity"
                  type="number"
                  :min="feature.min_quantity"
                  :max="feature.max_quantity"
                  :disabled="!feature.active"
                />
              </div>

              <div class="apps-gestao-total">
                <span>Total a consumir</span>
                <strong>{{ formatarMoedaLicenca(totalFeatureGestao(feature)) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="apps-gestao-footer">
          <div>
            <div class="font-weight-bold">
              Total a consumir: {{ formatarMoedaLicenca(totalConsumoAppsGestao) }}
            </div>
              <div class="table-muted">
                Saldo restante: {{ formatarMoedaLicenca(saldoRestanteAppsGestao) }}
              </div>
              <div class="table-muted">
                Multiempresa: {{ consumoLicencasMultiempresa }} licença(s),
                {{ devolucaoLicencasMultiempresa }} devolvida(s),
                {{ saldoLicencasMultiempresa }} restante(s)
              </div>
          </div>
          <div>
            <b-button
              variant="secondary"
              class="mr-2"
              :disabled="salvandoAppsClienteGestao"
              @click="$bvModal.hide('modal-gestao-apps-cliente-revenda')"
            >
              Cancelar
            </b-button>
            <b-button
              variant="primary"
              :disabled="
                salvandoAppsClienteGestao ||
                !featuresGestaoCliente.length ||
                consumoLicencasMultiempresa > licencasMultiempresaDisponiveis
              "
              @click="salvarAppsClienteGestao"
            >
              <b-spinner small class="mr-2" v-if="salvandoAppsClienteGestao" />
              {{ salvandoAppsClienteGestao ? "Salvando..." : "Salvar Apps" }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { SistemaMixin } from "@/modules/Application";

export default {
  name: "app-clientes-lista",
  mixins: [SistemaMixin],
  data() {
    return {
      clientes: [],
      revenda: {},
      totalClientes: 0,
      busca: "",
      filtroRapido: "todos",
      clienteAppsSelecionado: null,
      clienteAppsGestao: null,
      loadingAppsClienteGestao: false,
      salvandoAppsClienteGestao: false,
      featuresGestaoCliente: [],
      saldoAppsRevenda: {
        disponivel: 0,
        usado: 0,
      },
      licencasMultiempresaDisponiveis: 0,
      opcoesPeriodoMeses: [
        { value: 1, text: "1" },
        { value: 3, text: "3" },
        { value: 6, text: "6" },
        { value: 12, text: "12" },
        { value: 24, text: "24" },
      ],
      filtrosRapidos: [
        { value: "todos", text: "Todos" },
        { value: "ativos", text: "Ativos" },
        { value: "bloqueados", text: "Bloqueados" },
        { value: "com_apps", text: "Com apps" },
        { value: "sem_apps", text: "Sem apps" },
      ],
    };
  },
  async mounted() {
    await this.getDataInit();
  },
  methods: {
    ...mapActions(["setLoading"]),
    normalizarFeatureGestaoCliente(feature) {
      const cadastro = feature.feature || {};
      const billingMode = feature.billing_mode || cadastro.billing_mode || "flat";
      const minQuantity = Math.max(
        1,
        Number(feature.min_quantity || cadastro.min_quantity || 1)
      );
      const maxQuantity = Math.max(
        minQuantity,
        Number(feature.max_quantity || cadastro.max_quantity || 99)
      );

      return {
        code: feature.code || cadastro.code,
        name: feature.name || feature.nome || cadastro.name || cadastro.nome || "App",
        type: String(feature.type || feature.tipo || cadastro.type || "module").toLowerCase() === "plugin"
          ? "plugin"
          : "module",
        billing_mode: billingMode,
        monthly_price: this.valorNumericoLicenca(
          feature.monthly_price ??
            feature.valor_mensal ??
            cadastro.monthly_price ??
            cadastro.valor_mensal ??
            0
        ),
        active: this.valorBooleano(
          feature.active ?? feature.ativo ?? feature.enabled ?? feature.liberado
        ),
        quantity:
          billingMode === "per_quantity"
            ? Math.max(minQuantity, Number(feature.quantity || feature.quantidade || 1))
            : 1,
        months: Math.max(1, Number(feature.months || feature.meses || 12)),
        min_quantity: minQuantity,
        max_quantity: maxQuantity,
        originalActive: this.valorBooleano(
          feature.active ?? feature.ativo ?? feature.enabled ?? feature.liberado
        ),
        originalQuantity:
          billingMode === "per_quantity"
            ? Math.max(minQuantity, Number(feature.quantity || 1))
            : 1,
      };
    },
    quantidadeFeatureGestao(feature) {
      const minimo = Math.max(1, Number(feature.min_quantity || 1));
      const maximo = Math.max(minimo, Number(feature.max_quantity || 99));

      return Math.min(
        maximo,
        Math.max(minimo, Number(feature.quantity || minimo))
      );
    },
    totalFeatureGestao(feature) {
      if (!feature.active) return 0;

      const quantidade =
        feature.billing_mode === "per_quantity"
          ? this.quantidadeFeatureGestao(feature)
          : 1;
      const meses = Math.max(1, Number(feature.months || 12));

      return this.valorNumericoLicenca(feature.monthly_price) * quantidade * meses;
    },
    async carregarSaldoAppsRevenda() {
      const idRevenda = this.$route.params.id_revenda;
      if (!idRevenda) return;

      try {
        let response;

        try {
          response = await this.$http.get(`/api/revenda/${idRevenda}/apps/saldo`);
        } catch (e) {
          response = await this.$http.post(`/api/revenda/apps/saldo`, {
            id_revenda: idRevenda,
          });
        }

        const data = response?.data?.data ?? response?.data ?? {};
        this.licencasMultiempresaDisponiveis = Math.max(
          0,
          Number(data.wallet?.licencas_disponiveis || 0)
        );
        this.saldoAppsRevenda = {
          disponivel: this.valorNumericoLicenca(
            data.saldo_disponivel ?? data.disponivel ?? data.available ?? 0
          ),
          usado: this.valorNumericoLicenca(
            data.saldo_usado ?? data.usado ?? data.used ?? 0
          ),
        };
      } catch (e) {
        this.$toast.error("Erro ao carregar saldo de apps da revenda.");
      }
    },
    async abrirGestaoAppsCliente(cliente) {
      const idRevendaRota = Number(this.$route.params.id_revenda);
      const idRevendaCliente = Number(cliente?.id_revenda || idRevendaRota);

      if (idRevendaRota && idRevendaCliente && idRevendaRota !== idRevendaCliente) {
        this.$toast.error("Não é permitido liberar apps para cliente de outra revenda.");
        return;
      }

      this.clienteAppsGestao = cliente;
      this.loadingAppsClienteGestao = true;
      this.featuresGestaoCliente = [];
      this.$bvModal.show("modal-gestao-apps-cliente-revenda");

      try {
        await this.carregarSaldoAppsRevenda();
        const response = await this.$http.get(
          `/api/revenda/clientes/${cliente.id_empresa}/features`
        );
        const data = response?.data?.data ?? response?.data ?? {};
        const features = Array.isArray(data) ? data : data.features || data.items || [];

        this.featuresGestaoCliente = features
          .map((item) => this.normalizarFeatureGestaoCliente(item))
          .filter((item) => item.code);
      } catch (e) {
        this.$toast.error("Erro ao carregar apps do cliente.");
      } finally {
        this.loadingAppsClienteGestao = false;
      }
    },
    async salvarAppsClienteGestao() {
      if (!this.clienteAppsGestao?.id_empresa) return;

      const totalConsumo = this.totalConsumoAppsGestao;
      if (totalConsumo > this.saldoAppsRevenda.disponivel) {
        this.$toast.error(
          "Saldo insuficiente para liberar estes apps. Solicite mais créditos à Nuts."
        );
        return;
      }
      if (
        this.consumoLicencasMultiempresa >
        this.licencasMultiempresaDisponiveis
      ) {
        this.$toast.error(
          "Estoque de licenças insuficiente para o limite Multiempresa informado."
        );
        return;
      }

      this.salvandoAppsClienteGestao = true;
      try {
        const payload = {
          features: this.featuresGestaoCliente
            .filter((feature) => feature.code)
            .map((feature) => ({
              code: feature.code,
              active: Boolean(feature.active),
              quantity:
                feature.billing_mode === "per_quantity"
                  ? this.quantidadeFeatureGestao(feature)
                  : 1,
              months: Math.max(1, Number(feature.months || 12)),
            })),
        };

        await this.$http.put(
          `/api/revenda/clientes/${this.clienteAppsGestao.id_empresa}/features`,
          payload
        );

        this.$toast.success("Apps do cliente atualizados com sucesso!");
        this.$bvModal.hide("modal-gestao-apps-cliente-revenda");
        await this.getDataInit();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Erro ao salvar apps do cliente."
        );
      } finally {
        this.salvandoAppsClienteGestao = false;
      }
    },
    async getDataInit() {
      this.setLoading(true);

      try {
        const id = this.$route.params.id_revenda;
        const resposta = await this.getClientesByRevenda(id);
        const clientes = resposta?.clientes || [];

        this.clientes = clientes;
        this.totalClientes = resposta?.total ?? clientes.length;
        this.revenda = this.normalizarRevenda(resposta, clientes[0]);

        if (!clientes.length) {
          this.$toast.warning("Nenhum cliente vinculado a revenda!");
        }
      } catch (e) {
        this.$toast.error("Erro ao carregar clientes da revenda.");
      } finally {
        this.setLoading(false);
      }
    },
    normalizarRevenda(resposta, primeiroCliente) {
      const revenda = resposta?.revenda || {};
      const origem = primeiroCliente || {};

      return {
        nome: revenda.nome || origem.revenda_nome || null,
        fone: revenda.fone || origem.revenda_fone || null,
        uf: revenda.uf || origem.revenda_uf || null,
        cidade: revenda.cidade || origem.revenda_cidade || null,
        cep: revenda.cep || origem.revenda_cep || null,
        logradouro: revenda.logradouro || origem.revenda_logradouro || null,
        numero: revenda.numero || origem.revenda_numero || null,
        bairro: revenda.bairro || origem.revenda_bairro || null,
      };
    },
    clienteKey(cliente) {
      return cliente.id_empresa || cliente.cnpj || cliente.cliente;
    },
    cidadeUfCliente(cliente) {
      const cidade = cliente.empresa_cidade || cliente.cliente_cidade || cliente.cidade;
      const uf = cliente.empresa_uf || cliente.cliente_uf || cliente.uf;

      if (cidade && uf) return `${cidade} - ${uf}`;
      return cidade || uf || "Cidade não informada";
    },
    documentoCliente(cliente) {
      if (!cliente.cnpj) return "Não informado";

      try {
        return this.formatCpfCnpj(cliente.cnpj);
      } catch (e) {
        return cliente.cnpj;
      }
    },
    valorBooleano(value) {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value === 1;
      if (typeof value === "string") {
        return ["1", "true", "sim", "yes", "ativo", "bloqueado"].includes(
          value.toLowerCase()
        );
      }

      return false;
    },
    clienteAtivo(cliente) {
      return this.valorBooleano(cliente.status);
    },
    clienteBloqueado(cliente) {
      return this.valorBooleano(cliente.bloqueado);
    },
    formatarDataLista(data) {
      return data ? this.formatDate(data) : "Não informada";
    },
    resumoLicenca(row) {
      const resumo = row?.licenca_resumo || {};
      const plano = resumo.plano || {};
      const apps = resumo.apps || {};
      const totais = resumo.totais || {};
      const itensApps = Array.isArray(apps.itens) ? apps.itens : [];
      const valorPlanoResumo = this.valorNumericoLicenca(plano.valor_anual);
      const valorPlanoBase = this.valorNumericoLicenca(
        plano.base_valor_anual ?? resumo.plano_valor_anual ?? row?.valor ?? 0
      );
      const totalAnualApps = this.valorNumericoLicenca(apps.anual_total ?? 0);
      const totalMensalApps = this.valorNumericoLicenca(apps.mensal_total ?? 0);
      const totalAnualResumo = this.valorNumericoLicenca(
        totais.total_anual_licenca
      );
      const resumoInconsistente =
        valorPlanoResumo > 0 &&
        totalAnualResumo > 0 &&
        Math.abs(valorPlanoResumo + totalAnualApps - totalAnualResumo) > 0.01;
      const valorPlanoAnual =
        resumoInconsistente && valorPlanoBase > 0
          ? valorPlanoBase
          : valorPlanoResumo || valorPlanoBase;
      const totalAnualCalculado = valorPlanoAnual + totalAnualApps;
      const totalAtivos =
        Number(apps.total_ativos || 0) ||
        itensApps.filter((item) => this.appLicencaAtivo(item)).length;

      return {
        plano: {
          nome:
            plano.nome ||
            row?.plano ||
            row?.nome_plano ||
            this.obterNomePlanoLicenca(row?.id_plan),
          valor_anual: valorPlanoAnual,
          data_validade: plano.data_validade || row?.data_validade || null,
        },
        apps: {
          total_ativos: totalAtivos,
          anual_total: totalAnualApps,
          mensal_total: totalMensalApps,
          itens: itensApps,
        },
        totais: {
          total_anual_licenca:
            totalAnualResumo > 0 &&
            Math.abs(totalAnualResumo - totalAnualCalculado) <= 0.01
              ? totalAnualResumo
              : totalAnualCalculado,
          total_mensal_recorrente: this.valorNumericoLicenca(
            totais.total_mensal_recorrente ?? totalMensalApps
          ),
        },
      };
    },
    appLicencaAtivo(item) {
      const valor = item?.active ?? item?.ativo ?? item?.enabled ?? item?.liberado;

      if (typeof valor === "boolean") return valor;
      if (typeof valor === "number") return valor === 1;
      if (typeof valor === "string") {
        return ["1", "true", "sim", "yes", "ativo"].includes(valor.toLowerCase());
      }

      return true;
    },
    valorNumericoLicenca(valor) {
      if (valor === null || valor === undefined || valor === "") return 0;
      if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;

      const normalizado = String(valor)
        .replace(/[^\d,.-]/g, "")
        .replace(/\.(?=\d{3}(,|$))/g, "")
        .replace(",", ".");
      const numero = Number(normalizado);

      return Number.isFinite(numero) ? numero : 0;
    },
    primeiroValorNumericoLicenca(...valores) {
      for (const valor of valores) {
        const numero = this.valorNumericoLicenca(valor);
        if (numero > 0) return numero;
      }

      return 0;
    },
    formatarMoedaLicenca(valor) {
      return `R$ ${this.numberToReal(this.valorNumericoLicenca(valor), 2)}`;
    },
    obterNomePlanoLicenca(idPlan) {
      const planos = {
        1: "Free",
        2: "Essencial Anual",
        3: "Essencial Mensal",
        4: "Standard Mensal",
        6: "Standard Anual",
        7: "Basic",
      };

      return planos[Number(idPlan)] || "Sem plano";
    },
    nomePlanoLicenca(row) {
      return this.resumoLicenca(row).plano.nome || "Sem plano";
    },
    dataValidadeLicenca(row) {
      return this.resumoLicenca(row).plano.data_validade;
    },
    totalAppsAtivosLicenca(row) {
      return this.resumoLicenca(row).apps.total_ativos;
    },
    totalAnualAppsLicenca(row) {
      return this.resumoLicenca(row).apps.anual_total;
    },
    totalMensalAppsLicenca(row) {
      return this.resumoLicenca(row).apps.mensal_total;
    },
    totalAnualLicenca(row) {
      return this.resumoLicenca(row).totais.total_anual_licenca;
    },
    totalMensalRecorrenteLicenca(row) {
      return this.resumoLicenca(row).totais.total_mensal_recorrente;
    },
    appsAtivosLicenca(row) {
      return this.resumoLicenca(row).apps.itens.filter((item) =>
        this.appLicencaAtivo(item)
      );
    },
    nomeAppLicenca(app) {
      return (
        app.nome ||
        app.name ||
        app.titulo ||
        app.title ||
        app.codigo ||
        app.code ||
        "App"
      );
    },
    tipoAppLicenca(app) {
      const tipo = app.tipo || app.type || app.categoria || "Módulo";
      return String(tipo).toLowerCase() === "plugin" ? "Plugin" : "Módulo";
    },
    origemAppLicenca(app) {
      const origem = app.origem || app.source || app.renewal_origin;
      const nomes = {
        renewal: "Renovação",
        marketplace: "Marketplace",
        checkout: "Marketplace",
        manual: "Manual",
        legacy: "Legado",
        revenda: "Revenda",
      };

      return nomes[origem] || origem || "Não informada";
    },
    textoValorAppLicenca(app) {
      const valorAnual = this.primeiroValorNumericoLicenca(
        app.valor_anual,
        app.anual_total,
        app.total_anual,
        app.annual_price
      );
      const valorMensalUnitario = this.primeiroValorNumericoLicenca(
        app.valor_mensal,
        app.monthly_price
      );
      const valorMensalTotal = this.primeiroValorNumericoLicenca(
        app.mensal_total,
        app.total_mensal,
        valorMensalUnitario
      );
      const quantidade = Number(app.quantidade || app.quantity || 0);
      const valores = [];

      if (valorAnual > 0) {
        valores.push(`${this.formatarMoedaLicenca(valorAnual)}/ano`);
      }

      if (valorMensalTotal > 0) {
        if (
          quantidade > 1 &&
          valorMensalUnitario > 0 &&
          valorMensalTotal !== valorMensalUnitario
        ) {
          valores.push(
            `${quantidade} x ${this.formatarMoedaLicenca(
              valorMensalUnitario
            )} = ${this.formatarMoedaLicenca(valorMensalTotal)}/mês`
          );
        } else {
          valores.push(`${this.formatarMoedaLicenca(valorMensalTotal)}/mês`);
        }
      }

      return valores.length ? valores.join(" + ") : "Sem valor informado";
    },
    nomesAppsResumo(row) {
      const apps = this.appsAtivosLicenca(row);
      if (!apps.length) return "Detalhes dos apps não enviados";

      const nomes = apps.slice(0, 3).map((app) => this.nomeAppLicenca(app));
      const restante = apps.length - nomes.length;
      const sufixo = restante > 0 ? ` +${restante} apps` : "";

      return `${nomes.join(", ")}${sufixo}`;
    },
    abrirModalApps(cliente) {
      this.clienteAppsSelecionado = cliente;
      this.$bvModal.show("modal-apps-cliente-revenda");
    },
    textoBuscaCliente(cliente) {
      const apps = this.appsAtivosLicenca(cliente)
        .map((app) => this.nomeAppLicenca(app))
        .join(" ");

      return [
        cliente.cliente,
        cliente.cnpj,
        cliente.fone,
        cliente.empresa_cidade,
        cliente.cliente_cidade,
        cliente.cidade,
        cliente.empresa_uf,
        cliente.cliente_uf,
        cliente.uf,
        apps,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
  },
  computed: {
    enderecoRevenda() {
      const partes = [
        this.revenda.logradouro,
        this.revenda.numero,
        this.revenda.bairro,
        this.revenda.cep,
        this.revenda.cidade && this.revenda.uf
          ? `${this.revenda.cidade} - ${this.revenda.uf}`
          : this.revenda.cidade || this.revenda.uf,
      ].filter(Boolean);

      return partes.length ? partes.join(", ") : "Não informado";
    },
    metricas() {
      return {
        total: this.totalClientes || this.clientes.length,
        ativos: this.clientes.filter((cliente) => this.clienteAtivo(cliente)).length,
        bloqueados: this.clientes.filter((cliente) =>
          this.clienteBloqueado(cliente)
        ).length,
        comApps: this.clientes.filter(
          (cliente) => this.totalAppsAtivosLicenca(cliente) > 0
        ).length,
      };
    },
    clientesFiltrados() {
      const termo = this.busca
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

      return this.clientes.filter((cliente) => {
        const totalApps = this.totalAppsAtivosLicenca(cliente);

        if (this.filtroRapido === "ativos" && !this.clienteAtivo(cliente)) {
          return false;
        }
        if (
          this.filtroRapido === "bloqueados" &&
          !this.clienteBloqueado(cliente)
        ) {
          return false;
        }
        if (this.filtroRapido === "com_apps" && totalApps <= 0) return false;
        if (this.filtroRapido === "sem_apps" && totalApps > 0) return false;
        if (
          this.filtroRapido === "apps_mensais" &&
          this.totalMensalAppsLicenca(cliente) <= 0
        ) {
          return false;
        }
        if (
          this.filtroRapido === "apps_anuais" &&
          this.totalAnualAppsLicenca(cliente) <= 0
        ) {
          return false;
        }

        if (!termo) return true;

        return this.textoBuscaCliente(cliente).includes(termo);
      });
    },
    appsClienteSelecionado() {
      if (!this.clienteAppsSelecionado) return [];
      return this.appsAtivosLicenca(this.clienteAppsSelecionado);
    },
    totalConsumoAppsGestao() {
      return this.featuresGestaoCliente.reduce(
        (acc, feature) => acc + this.totalFeatureGestao(feature),
        0
      );
    },
    saldoRestanteAppsGestao() {
      return Math.max(0, this.saldoAppsRevenda.disponivel - this.totalConsumoAppsGestao);
    },
    consumoLicencasMultiempresa() {
      const feature = this.featuresGestaoCliente.find(
        (item) => item.code === "PLG_MULTIEMPRESA"
      );
      if (!feature || !feature.active) return 0;

      const quantidade = this.quantidadeFeatureGestao(feature);

      return feature.originalActive
        ? Math.max(0, quantidade - Number(feature.originalQuantity || 0))
        : quantidade;
    },
    devolucaoLicencasMultiempresa() {
      const feature = this.featuresGestaoCliente.find(
        (item) => item.code === "PLG_MULTIEMPRESA"
      );
      if (!feature || !feature.active || !feature.originalActive) return 0;

      return Math.max(
        0,
        Number(feature.originalQuantity || 0) -
          this.quantidadeFeatureGestao(feature)
      );
    },
    saldoLicencasMultiempresa() {
      return Math.max(
        0,
        this.licencasMultiempresaDisponiveis -
          this.consumoLicencasMultiempresa +
          this.devolucaoLicencasMultiempresa
      );
    },
  },
};
</script>

<style scoped>
.revenda-clientes-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reseller-summary {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 18px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.summary-kicker {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.reseller-summary h2 {
  color: #181c32;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
}

.summary-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #5e6278;
  font-size: 13px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 14px;
}

.metric-card span {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.metric-card strong {
  display: block;
  color: #181c32;
  font-size: 24px;
  margin-top: 4px;
}

.list-controls {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 14px;
}

.search-input {
  max-width: 520px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.filter-chip {
  border: 1px solid #d8dbe7;
  background: #fff;
  border-radius: 16px;
  color: #5e6278;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 8px 12px;
}

.filter-chip.ativo {
  border-color: #0d6efd;
  background: #ecf4ff;
  color: #0d6efd;
}

.table-shell {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  overflow: hidden;
}

.reseller-client-table {
  margin: 0;
}

.reseller-client-table th {
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  border-top: none;
}

.reseller-client-table td {
  color: #3f4254;
  font-size: 13px;
  vertical-align: top;
}

.client-name,
.plan-name,
.apps-count {
  color: #181c32;
  font-weight: 700;
}

.table-muted {
  color: #7e8299;
  font-size: 12px;
}

.apps-names {
  color: #5e6278;
  font-size: 12px;
  max-width: 220px;
}

.license-line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-width: 150px;
  margin-bottom: 4px;
}

.license-line span {
  color: #7e8299;
}

.license-line strong {
  color: #181c32;
  text-align: right;
}

.status-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.empty-state {
  color: #7e8299;
  font-size: 14px;
  font-weight: 600;
  padding: 32px;
  text-align: center;
}

.empty-state.compact {
  padding: 18px;
}

.apps-modal-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-detail-item {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.app-type {
  background: #f3f6f9;
  border-radius: 4px;
  color: #5e6278;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  margin-left: 8px;
  padding: 3px 6px;
  text-transform: uppercase;
}

.app-price {
  color: #181c32;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.apps-gestao-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.apps-gestao-header {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #fcfcfd;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.apps-gestao-saldo {
  font-size: 20px;
  color: #181c32;
}

.apps-gestao-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 54vh;
  overflow-y: auto;
}

.apps-gestao-item {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.apps-gestao-main {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}

.apps-gestao-controls {
  display: grid;
  grid-template-columns: 140px 140px 1fr;
  gap: 10px;
  align-items: end;
  min-width: 420px;
}

.apps-gestao-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}

.apps-gestao-total {
  text-align: right;
}

.apps-gestao-total span {
  display: block;
  font-size: 12px;
  color: #7e8299;
}

.apps-gestao-total strong {
  color: #181c32;
  font-size: 15px;
}

.apps-gestao-footer {
  border-top: 1px solid #e4e6ef;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

@media (max-width: 991px) {
  .reseller-summary {
    flex-direction: column;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .apps-gestao-header,
  .apps-gestao-item,
  .apps-gestao-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .apps-gestao-controls {
    min-width: 0;
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .metric-grid {
    grid-template-columns: 1fr;
  }

  .app-detail-item {
    flex-direction: column;
  }

  .app-price {
    text-align: left;
    white-space: normal;
  }
}
</style>
